'use client';

import { useEffect, useState } from 'react';
import { useRouter, usePathname, useSearchParams } from 'next/navigation';
import { useAuth } from '@/contexts/auth-context';
import { logger } from '@/lib/logger';
import { useSessionToken } from '@/hooks/use-session-token';
import {
  PENDING_SESSION_MARKER_PARAM,
  PENDING_SESSION_MARKER_VALUE,
} from '@/lib/auth/session-marker';

/**
 * ProtectedRoute component with optimistic rendering.
 *
 * Renders children optimistically while checking auth in the background.
 * This improves LCP by allowing page structure to render immediately
 * instead of blocking on auth checks.
 *
 * Only redirects to login after confirming the user is unauthenticated.
 */
export function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const [, setAuthTimeout] = useState(false);
  const [shouldRedirect, setShouldRedirect] = useState(false);

  // Check if a pending-session marker exists in URL (for immediate state)
  const hasSessionTokenInUrl =
    typeof window !== 'undefined' &&
    new URLSearchParams(window.location.search).get(PENDING_SESSION_MARKER_PARAM) ===
      PENDING_SESSION_MARKER_VALUE;

  // Kick off session token exchange immediately (even before children render)
  const { isExchanging: isSessionTokenExchanging } = useSessionToken();

  // We are waiting if the hook says so OR if we see the token in URL but hook hasn't started yet
  // This prevents the brief "not exchanging yet" state between mount and hook effect
  const waitingForSessionToken = isSessionTokenExchanging || hasSessionTokenInUrl;

  // Add timeout to prevent infinite loading (10 seconds) when not processing session token
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (loading && !waitingForSessionToken) {
        logger.warn('Auth loading timeout - redirecting to login');
        setAuthTimeout(true);
        setShouldRedirect(true);
      }
    }, 10000);

    return () => clearTimeout(timeout);
  }, [loading, waitingForSessionToken]);

  // Handle redirect in background - don't block rendering
  useEffect(() => {
    // Don't redirect if we're waiting for session token exchange or auth is loading
    if (waitingForSessionToken || loading) return;

    // If user became available (e.g., after slow auth resolution), cancel any pending redirect
    if (user) {
      if (shouldRedirect) {
        setShouldRedirect(false);
      }
      return;
    }

    // Only redirect if we're sure there's no user and no session token being processed
    // Check if the pending-session marker is in the URL - if so, wait for it to be processed
    if (typeof window !== 'undefined') {
      const urlParams = new URLSearchParams(window.location.search);
      if (urlParams.get(PENDING_SESSION_MARKER_PARAM) === PENDING_SESSION_MARKER_VALUE) {
        // Session exchange is pending, wait for it to be processed
        return;
      }
    }
    // Set redirect flag - will redirect in next effect
    setShouldRedirect(true);
  }, [user, loading, waitingForSessionToken, shouldRedirect]);

  // Perform redirect separately to avoid blocking render
  useEffect(() => {
    if (shouldRedirect) {
      // Preserve the originally-requested path (and query string, e.g. a deep-linked
      // thread id) so login/page.tsx can send the user back where they meant to go
      // instead of always landing on /dashboard.
      const query = searchParams.toString();
      const currentPath = query ? `${pathname}?${query}` : pathname;
      router.push(`/login?next=${encodeURIComponent(currentPath)}`);
    }
  }, [shouldRedirect, router, pathname, searchParams]);

  // Render children optimistically - auth check happens in background
  // This allows page structure to render immediately for better LCP
  return <>{children}</>;
}
